import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HotspotPlan from "./HotspotPlan";
import { TreePine, ListChecks, ShieldCheck, Phone, ChevronsDown, GraduationCap, Dumbbell, ShoppingBag, ShoppingCart, Fuel, Building2, MapPin, X, ExternalLink, Map as MapIcon, ArrowRight,
  LandPlot, CircleDot, Volleyball, MountainSnow, Disc, Flame, Droplets, TentTree,
  Snowflake, Home, Sparkles, Grid2x2, Utensils, Baby, Tent, Activity, Mountain,
  Goal, Waves, WavesLadder, Target, Footprints } from "lucide-react";

export const DEER_PARK_REGION = { x: 0.191, y: 0.018, w: 0.151, h: 0.217, label: "Deer Park Estates" };

const gmaps = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;

// closest schools / rec / our commercial zone (approx. positions on the city map)
const POIS = [
  { x: 0.078, y: 0.305, icon: GraduationCap, color: "#2E6FB0", label: "Copperhaven School (K–9)", name: "Copperhaven School", type: "Closest elementary & middle · K–9", address: "151 Grove Drive West, Spruce Grove, AB", website: "https://copperhaven.psd.ca" },
  { x: 0.445, y: 0.31, icon: GraduationCap, color: "#2E6FB0", label: "Spruce Grove Composite HS", name: "Spruce Grove Composite High School", type: "Closest high school · Grades 10–12", address: "1000 Calahoo Road, Spruce Grove, AB", website: "https://sgchs.psd.ca" },
  { x: 0.175, y: 0.45, icon: Dumbbell, color: "#C9772E", label: "TransAlta Tri Leisure Centre", name: "TransAlta Tri Leisure Centre", type: "Recreation centre · Jennifer Heil Way", address: "221 Jennifer Heil Way, Spruce Grove, AB", website: "https://www.trileisure.com" },
  { x: 0.19, y: 0.245, icon: ShoppingBag, color: "#B0472E", label: "Commercial Centre", name: "Deer Park Commercial Centre", type: "Our retail & services zone within Deer Park · opening ~2027", address: "Jennifer Heil Way & Dalton Link, Spruce Grove, AB", internal: "/commercial" },
  { x: 0.205, y: 0.215, icon: Fuel, color: "#2E7D6B", label: "Co-op Gas & Tim Hortons", name: "Co-op Gas Bar & Tim Hortons", type: "24 hr Co-op gas + Tim Hortons drive-thru · in Deer Park, by the shopping centre", address: "1 Dalton Link, Spruce Grove, AB T7X 3E8", website: "https://locations.timhortons.ca/en/ab/spruce-grove/1-dalton-link/" },
  { x: 0.10, y: 0.41, icon: ShoppingCart, color: "#C0392B", label: "Real Canadian Superstore", name: "Real Canadian Superstore", type: "Groceries, pharmacy & gas · closest full-size grocery", address: "110 Jennifer Heil Way, Spruce Grove, AB", website: "https://www.realcanadiansuperstore.ca" },
  { x: 0.045, y: 0.30, icon: Building2, color: "#005DAA", label: "Costco · Stony Plain ~2027", name: "Costco — coming to Stony Plain", type: "Under construction west of Spruce Grove · Hwy 16A & Veterans Blvd, Stony Plain · opening ~Summer 2027", address: "Stony Plain Power Centre, Stony Plain, AB", website: "https://www.costco.ca" },
  { x: 0.255, y: 0.20, icon: Baby, color: "#C2568B", label: "Discovery Land Daycare", name: "Discovery Land Daycare", type: "Licensed childcare & daycare · right inside Deer Park", address: "Deer Park, Spruce Grove, AB", website: "https://maps.app.goo.gl/1cnBDhQpDf5axAa3A" },
  { x: 0.265, y: 0.218, icon: ShoppingBag, color: "#B0472E", label: "Deer Park Shopping Centre", name: "Deer Park Shopping Centre", type: "Neighbourhood plaza · Deerpark Liquor, Tim Hortons, Co-op & more", address: "Deer Park, Spruce Grove, AB", website: "https://maps.app.goo.gl/PHtF3bapPuY5z3JJ7" },
];

// Highlighted routes drawn over the city map (rendered as running dashed lines).
const ROUTES = [
  // Jennifer Heil Way — arterial linking Deer Park up to the Yellowhead
  { points: [[0.182, 0.02], [0.181, 0.30], [0.179, 0.50], [0.176, 0.62]], color: "#C9B79C", width: 1.7, arrow: true },
  // Costco is off-map to the west (Stony Plain) — arrow pointing west
  { points: [[0.088, 0.30], [0.018, 0.30]], color: "#005DAA", width: 1.5, dash: "3 2", arrow: true },
];

const AMENITIES = [
  "Agrena — Grant Fuhr & Stu Barnes Arena / Spruce Grove Curling Club",
  "Aspenglen Park",
  "Beechmont Park",
  "Border Paving Athletic Centre",
  "Brookside Park",
  "Brookwood School",
  "Central Park and Lions Log Cabin",
  "Cpl. Jim Galloway Off-Leash Park",
  "Columbus Park",
  "Deer Park",
  "Diamond Grove RV Campground",
  "École Broxton Park School",
  "Elks Hall",
  "Fairway Park",
  "Fieldstone Park",
  "Fuhr Sports Park",
  "Greystone Park",
  "Greystone Centennial Middle School",
  "Harvest Ridge Park",
  "Heatherglen Park",
  "Henderson Park",
  "Henry Singer Ball Park, Tennis & Pickleball Courts",
  "Heritage Grove Park",
  "Horizon Stage",
  "Jubilee Park",
  "Lakewood Park",
  "Landsdowne Park",
  "Lindsay Park",
  "Links at Spruce Grove",
  "Living Waters Christian Academy",
  "Longview Park",
  "Marlboro Off-Leash Park",
  "McKean Park",
  "McLeod Avenue Park",
  "Melcor Cultural Centre (Art Gallery & Library)",
  "Millgrove School",
  "Naturescape",
  "Pioneer Centre",
  "Prescott Learning Centre",
  "Rick Delorme Baseball Diamond",
  "Rotary Centennial Park",
  "Rotary Playscape",
  "Skateboard Park",
  "South Century Driving Range",
  "Spruce Grove Composite High School",
  "St. Joseph Catholic School",
  "St. Thomas Aquinas Catholic School",
  "St. Peter the Apostle Catholic High School",
  "St. Marguerite Catholic School",
  "TransAlta Tri Leisure Centre",
  "Victoria Park",
  "Westgrove Park",
  "Westview Park",
  "Windermere Park",
  "Woodhaven Middle School",
  "Woodside Park",
];

const LEGEND = [
  { label: "Ball Diamonds", Icon: LandPlot },
  { label: "Basketball", Icon: CircleDot },
  { label: "Beach Volleyball", Icon: Volleyball },
  { label: "Cross-Country Skiing", Icon: MountainSnow },
  { label: "Disc Golf", Icon: Disc },
  { label: "Fire Pit", Icon: Flame },
  { label: "Fountain", Icon: Droplets },
  { label: "Gazebo", Icon: TentTree },
  { label: "Outdoor Rinks", Icon: Snowflake },
  { label: "Indoor Rinks", Icon: Home },
  { label: "Leisure Ice", Icon: Sparkles },
  { label: "Pickle Ball", Icon: Grid2x2 },
  { label: "Picnic", Icon: Utensils },
  { label: "Playgrounds", Icon: Baby },
  { label: "Shelter", Icon: Tent },
  { label: "Skateboarding", Icon: Activity },
  { label: "Sledding Hills", Icon: Mountain },
  { label: "Sport Fields", Icon: Goal },
  { label: "Spray Park", Icon: Waves },
  { label: "Swimming Pool", Icon: WavesLadder },
  { label: "Tennis", Icon: Target },
  { label: "Track", Icon: Footprints },
];

/**
 * Redesigned City of Spruce Grove area map: large map on the left (fully
 * visible, no inner scroll) with a clean, vertically-scrollable amenities /
 * legend panel on the right (no horizontal scroll).
 */
export const AreaMapView = ({ mapSrc, panelHeight = "lg:max-h-[74vh]" }) => {
  const scrollRef = useRef(null);
  const [showHint, setShowHint] = useState(false);
  const [poi, setPoi] = useState(null);
  const [poiPos, setPoiPos] = useState(null);
  const navigate = useNavigate();

  const openPoi = (p, el) => {
    setPoi(p);
    if (el && el.getBoundingClientRect) {
      const r = el.getBoundingClientRect();
      setPoiPos({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
    } else setPoiPos(null);
  };

  const check = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowHint(el.scrollHeight - el.scrollTop - el.clientHeight > 12);
  };
  useEffect(() => {
    const t = setTimeout(check, 200);
    window.addEventListener("resize", check);
    return () => { clearTimeout(t); window.removeEventListener("resize", check); };
  }, [mapSrc]);
  const scrollDown = () => scrollRef.current?.scrollBy({ top: (scrollRef.current.clientHeight || 300) * 0.8, behavior: "smooth" });

  if (!mapSrc) return null;

  // The area map image was cropped at Century Road (kept left 71.5%) and along
  // McLeod Avenue (kept top 65.5%); rescale fractional coords to the new frame.
  const XCUT = 0.715;
  const YCUT = 0.655;
  const sx = (x) => x / XCUT;
  const sy = (y) => y / YCUT;
  const poisFit = POIS.map((p) => ({ ...p, x: sx(p.x), y: sy(p.y) }));
  const regionFit = { ...DEER_PARK_REGION, x: sx(DEER_PARK_REGION.x), y: sy(DEER_PARK_REGION.y), w: DEER_PARK_REGION.w / XCUT, h: DEER_PARK_REGION.h / YCUT };
  const routesFit = ROUTES.map((r) => ({ ...r, points: r.points.map(([x, y]) => [sx(x), sy(Math.min(y, YCUT - 0.01))]) }));

  const CARD_W = 300;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const pxc = poiPos?.x ?? vw / 2;
  const pyc = poiPos?.y ?? 140;
  const poiLeft = pxc > vw / 2 ? Math.max(pxc - CARD_W - 18, 8) : Math.min(pxc + 18, vw - CARD_W - 8);
  const poiTop = Math.min(Math.max(pyc - 50, 74), vh - 240);

  return (
    <div className="flex flex-col lg:flex-row gap-3" data-testid="area-map-view">
      <div className="lg:flex-1 min-w-0">
        <HotspotPlan src={mapSrc} region={regionFit} pois={poisFit} routes={routesFit} onPoiClick={openPoi} fit />
      </div>

      {/* POI info popup (Google-Maps style quick card) */}
      {poi && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setPoi(null)} data-testid="poi-popup-overlay" />
          <div style={{ position: "fixed", left: poiLeft, top: poiTop, width: CARD_W }} className="z-50 rounded-2xl border border-[#F3EEE3]/15 bg-[#12201A] p-5 shadow-[0_14px_48px_rgba(0,0,0,0.6)]" data-testid="poi-popup">
            <button onClick={() => setPoi(null)} className="absolute top-3 right-3 text-[#F3EEE3]/60 hover:text-[#F3EEE3]" data-testid="poi-popup-close"><X size={17} /></button>
            <div className="flex items-center gap-2.5">
              <span className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: poi.color }}>{poi.icon ? <poi.icon size={16} className="text-white" /> : null}</span>
              <p className="font-display text-lg text-[#F3EEE3] leading-tight pr-4">{poi.name}</p>
            </div>
            <p className="text-[#C9B79C] text-[0.7rem] mt-2 leading-snug">{poi.type}</p>
            {poi.address && <p className="flex items-start gap-2 text-[#F3EEE3]/70 text-[0.78rem] mt-3 leading-snug"><MapPin size={13} className="text-[#C9B79C] shrink-0 mt-0.5" /> {poi.address}</p>}
            <div className="mt-4 space-y-2">
              {poi.internal ? (
                <button onClick={() => navigate(poi.internal)} data-testid="poi-popup-internal" className="w-full inline-flex items-center justify-center gap-2 bg-[#F3EEE3] text-[#12201A] px-4 py-2.5 rounded-full text-[0.8rem] font-medium hover:bg-white transition-colors">View our commercial centre <ArrowRight size={14} /></button>
              ) : (
                <>
                  {poi.website && <a href={poi.website} target="_blank" rel="noreferrer" data-testid="poi-popup-website" className="w-full inline-flex items-center justify-center gap-2 bg-[#F3EEE3] text-[#12201A] px-4 py-2.5 rounded-full text-[0.8rem] font-medium hover:bg-white transition-colors"><ExternalLink size={14} /> Visit website</a>}
                  <a href={gmaps(`${poi.name} ${poi.address || "Spruce Grove"}`)} target="_blank" rel="noreferrer" data-testid="poi-popup-gmaps" className="w-full inline-flex items-center justify-center gap-2 border border-[#F3EEE3]/25 text-[#F3EEE3] px-4 py-2.5 rounded-full text-[0.8rem] hover:border-[#C9B79C]/60 transition-colors"><MapIcon size={14} /> Rating &amp; reviews on Google</a>
                </>
              )}
            </div>
          </div>
        </>
      )}

      <aside className="lg:w-[323px] xl:w-[357px] shrink-0 lg:self-start rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] overflow-hidden flex flex-col" data-testid="area-info-panel">
        <div className="px-5 py-4 border-b border-[#F3EEE3]/10 shrink-0">
          <p className="eyebrow text-[0.55rem] text-[#C9B79C] flex items-center gap-2"><TreePine size={12} /> Parks &amp; Recreation</p>
          <p className="font-display text-lg text-[#F3EEE3] leading-snug mt-1">Amenities &amp; legend</p>
        </div>

        <div className="relative flex flex-col min-h-0">
          <div ref={scrollRef} onScroll={check} className={`overflow-y-auto max-h-[52vh] ${panelHeight} px-5 py-4 space-y-6`} data-testid="area-info-scroll">
          {/* nearby key amenities */}
          <div data-testid="area-nearby">
            <p className="eyebrow text-[0.55rem] text-[#C9B79C] mb-3 flex items-center gap-2"><MapPin size={12} /> Nearby Deer Park <span className="text-[#F3EEE3]/35 normal-case tracking-normal">(approx.)</span></p>
            <div className="space-y-2">
              {POIS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <button key={i} type="button" onClick={(e) => openPoi(p, e.currentTarget)} data-testid={`nearby-item-${i}`} className="w-full text-left flex items-start gap-3 rounded-xl border border-[#F3EEE3]/12 bg-[#0C1510] p-2.5 hover:border-[#C9B79C]/45 transition-colors">
                    <span className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: p.color }}><Icon size={14} className="text-white" /></span>
                    <div className="min-w-0">
                      <p className="text-[#F3EEE3] text-[0.8rem] font-medium leading-snug">{p.name}</p>
                      <p className="text-[#F3EEE3]/50 text-[0.68rem] leading-snug">{p.type}</p>
                    </div>
                  </button>
                );
              })}
              <p className="text-[#F3EEE3]/40 text-[0.62rem] leading-snug pt-0.5">Discovery Land Daycare offers licensed childcare right inside Deer Park; before/after-school care is also available at Copperhaven School.</p>
            </div>
          </div>

          {/* amenities */}
          <div>
            <p className="eyebrow text-[0.55rem] text-[#C9B79C] mb-3 flex items-center gap-2"><ListChecks size={12} /> Parks &amp; Recreation Amenities</p>
            <ol className="space-y-1.5">
              {AMENITIES.map((name, i) => (
                <li key={i} data-testid={`amenity-item-${i + 1}`} className="flex gap-2.5 text-[0.82rem] leading-snug text-[#F3EEE3]/80">
                  <span className="shrink-0 w-5 text-right font-display text-[#C9B79C]">{i + 1}</span>
                  <span>{name}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* recreation legend */}
          <div className="pt-1 border-t border-[#F3EEE3]/10">
            <p className="eyebrow text-[0.55rem] text-[#C9B79C] mb-3 mt-4">Recreation Legend</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {LEGEND.map(({ label, Icon }, i) => (
                <div key={i} data-testid={`legend-item-${i}`} className="flex items-center gap-2 text-[0.8rem] text-[#F3EEE3]/75 leading-snug">
                  <Icon size={14} className="shrink-0 text-[#7FB79A]" />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* heritage grove */}
          <div className="rounded-xl border border-[#C9B79C]/25 bg-[#C9B79C]/10 p-4">
            <p className="font-display text-[#F3EEE3] text-base flex items-center gap-2 leading-snug"><ShieldCheck size={15} className="text-[#C9B79C]" /> Heritage Grove Park Protection System</p>
            <p className="text-[#F3EEE3]/70 text-[0.8rem] leading-relaxed mt-2">Throughout the park you will find emergency locator posts. In an emergency, dial 911 and tell the operator the number of the nearest post so crews can find your exact location for a faster response.</p>
            <a href="tel:7809622611" className="inline-flex items-center gap-2 text-[#C9B79C] text-[0.8rem] mt-3 hover:text-[#F3EEE3]"><Phone size={13} /> 780-962-2611 — report maintenance or vandalism</a>
          </div>
          </div>
          <div className={`pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-[#0F1B15] to-transparent transition-opacity duration-300 ${showHint ? "opacity-100" : "opacity-0"}`} />
          {showHint && (
            <button type="button" onClick={scrollDown} data-testid="area-info-scrollhint" aria-label="Scroll for more" className="absolute bottom-2.5 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 rounded-full bg-[#C9B79C] text-[#12201A] text-[0.6rem] font-semibold uppercase tracking-wide px-3 py-1.5 shadow-[0_6px_18px_rgba(0,0,0,0.45)] animate-bounce">
              More <ChevronsDown size={13} />
            </button>
          )}
        </div>

        <p className="px-5 py-2.5 text-[0.6rem] text-[#F3EEE3]/40 border-t border-[#F3EEE3]/10 shrink-0">Source: City of Spruce Grove</p>
      </aside>
    </div>
  );
};

export default AreaMapView;
